import React, { useState, useEffect } from 'react';
import { useTeamStore } from '../../store/teamStore';
import { playerCardService } from '../../services/playerCardService';
import { playerAttributeService } from '../../services/playerAttributeService';
import { supabase } from '../../lib/supabase';
import type { PlayerCard } from '../../types/playerCard';
import { Users, Search, Plus, Star, User, Filter, X, Grid3x3, List, Shield, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { InjuryBadge } from '../ui/InjuryBadge';
import type { InjuryStatus } from '../../constants/injuries';
import { PositionDisplay } from './PositionDisplay';
import { getAllPositionGroupKeys, getPositionGroupsForSport } from '../../data/positionHierarchy';
import { ArrowUpDown } from 'lucide-react';

type SortOption = 'number' | 'lastName' | 'position' | 'ovr';
type ViewMode = 'tiles' | 'list';

export const PlayerCardList: React.FC = () => {
  const { currentTeam } = useTeamStore();
  const [playerCards, setPlayerCards] = useState<PlayerCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPosition, setFilterPosition] = useState<string>('all');
  const [filterHealth, setFilterHealth] = useState<string>('all');
  const [filterMPR, setFilterMPR] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortOption>('number');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  useEffect(() => {
    if (currentTeam?.id) {
      loadPlayerCards();
    }
  }, [currentTeam?.id]);

  const loadPlayerCards = async () => {
    if (!currentTeam?.id) return;

    setLoading(true);
    try {
      const cards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
      
      // Also fetch players if not loaded (including play_participation for MPR)
      const { data: players } = await supabase
        .from('players')
        .select('*, play_participation')
        .eq('team_id', currentTeam.id);

      // Use saved designations from cards (no need to regenerate on every load)
      const cardsWithPlayers = cards.map((card) => {
        const player = players?.find(p => p.id === card.player_id);
        // Map play_participation to playParticipation for consistency
        const playerWithParticipation = player ? {
          ...player,
          playParticipation: player.play_participation
        } : undefined;
        return { ...card, player: playerWithParticipation };
      });

      setPlayerCards(cardsWithPlayers);
    } catch (error) {
      console.error('Error loading player cards:', error);
      toast.error('Failed to load player cards');
    } finally {
      setLoading(false);
    }
  };

  // Get all position groups for the current sport
  const allPositionGroups = currentTeam?.sport 
    ? getAllPositionGroupKeys(currentTeam.sport)
    : [];

  // Filter and search
  const filteredCards = playerCards.filter(card => {
    const matchesSearch = searchQuery === '' || 
      `${card.player?.first_name} ${card.player?.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      card.player?.number?.toString().includes(searchQuery);

    const matchesPosition = filterPosition === 'all' || 
      card.eligible_positions.includes(filterPosition);
    
    // Use player's injury_status (defaults to 'H' if not set)
    const injuryStatus = (card.player?.injury_status as InjuryStatus) || 'H';
    const matchesHealth = filterHealth === 'all' || 
      (filterHealth === 'healthy' && injuryStatus === 'H') ||
      (filterHealth === 'limited' && (injuryStatus === 'Q' || injuryStatus === 'D')) ||
      (filterHealth === 'no_contact' && injuryStatus === 'O');

    // MPR filter
    const hasMPR = card.player?.playParticipation?.hasMPR || false;
    const matchesMPR = filterMPR === 'all' || 
      (filterMPR === 'yes' && hasMPR) ||
      (filterMPR === 'no' && !hasMPR);

    return matchesSearch && matchesPosition && matchesHealth && matchesMPR;
  });

  // Sort comparator for cards
  const sortCards = (a: PlayerCard, b: PlayerCard) => {
    switch (sortBy) {
      case 'number': {
        const numA = a.player?.number ?? 999;
        const numB = b.player?.number ?? 999;
        return numA - numB;
      }
      case 'lastName': {
        const lastNameA = (a.player?.last_name || '').toLowerCase();
        const lastNameB = (b.player?.last_name || '').toLowerCase();
        return lastNameA.localeCompare(lastNameB);
      }
      case 'position': {
        const posA = a.eligible_positions[0] || 'ZZZ';
        const posB = b.eligible_positions[0] || 'ZZZ';
        return posA.localeCompare(posB);
      }
      case 'ovr': {
        const ovrA = a.ovr_rating ?? 0;
        const ovrB = b.ovr_rating ?? 0;
        return ovrB - ovrA;
      }
      default:
        return 0;
    }
  };

  // Split into main roster and subs; main roster first for playing time / batting order priority
  const mainRosterCards = [...filteredCards].filter((card) => !(card.player as any)?.is_sub);
  const subCards = [...filteredCards].filter((card) => (card.player as any)?.is_sub === true);
  const sortedMainRoster = mainRosterCards.sort(sortCards);
  const sortedSubs = subCards.sort(sortCards);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-600">Loading player cards...</p>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Please select a team to view player cards.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar and Filter Toggle */}
      <div className="bg-white rounded-lg shadow-md p-3">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name or number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="List View"
            >
              <List size={18} />
            </button>
            <button
              onClick={() => setViewMode('tiles')}
              className={`px-3 py-2 transition-colors border-l border-gray-300 ${
                viewMode === 'tiles'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="Tile View"
            >
              <Grid3x3 size={18} />
            </button>
          </div>

          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
              showFilters || filterPosition !== 'all' || filterHealth !== 'all' || filterMPR !== 'all' || sortBy !== 'number'
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter size={18} />
            <span className="text-sm font-medium">Filters</span>
            {(filterPosition !== 'all' || filterHealth !== 'all' || filterMPR !== 'all' || sortBy !== 'number') && (
              <span className="bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                {(filterPosition !== 'all' ? 1 : 0) + (filterHealth !== 'all' ? 1 : 0) + (filterMPR !== 'all' ? 1 : 0) + (sortBy !== 'number' ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Filters Flyout */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Position Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
                <select
                  value={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Positions</option>
                  {allPositionGroups.map((groupKey) => {
                    const groups = getPositionGroupsForSport(currentTeam?.sport || 'Football');
                    if (!groups) return null;
                    
                    // Find the group name
                    let groupName = groupKey;
                    for (const unit of groups.units) {
                      const group = unit.groups.find(g => g.key === groupKey);
                      if (group) {
                        groupName = group.name;
                        break;
                      }
                    }
                    
                    return (
                      <option key={groupKey} value={groupKey}>
                        {groupName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Injury Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Health Status</label>
                <select
                  value={filterHealth}
                  onChange={(e) => setFilterHealth(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="healthy">Healthy</option>
                  <option value="limited">Questionable/Doubtful</option>
                  <option value="no_contact">Out</option>
                </select>
              </div>

              {/* MPR Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">MPR Status</label>
                <select
                  value={filterMPR}
                  onChange={(e) => setFilterMPR(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Players</option>
                  <option value="yes">Has MPR</option>
                  <option value="no">No MPR</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Sort By</label>
                <div className="relative">
                  <ArrowUpDown className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
                  >
                    <option value="number">Number</option>
                    <option value="lastName">Last Name</option>
                    <option value="position">Position</option>
                    <option value="ovr">OVR Rating</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Player Cards: Main Roster then Subs */}
      {sortedMainRoster.length === 0 && sortedSubs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {playerCards.length === 0 ? 'No Player Cards Yet' : 'No Cards Match Filters'}
          </h3>
          <p className="text-gray-600 mb-6">
            {playerCards.length === 0
              ? 'Create player cards to track player development, attributes, and goals.'
              : 'Try adjusting your search or filters.'}
          </p>
          {playerCards.length === 0 && (
            <Link
              to={`/player-cards/new`}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Create First Player Card
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Main Roster */}
          {sortedMainRoster.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Roster</h3>
              {viewMode === 'tiles' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {sortedMainRoster.map((card) => renderCardTile(card))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="divide-y divide-gray-200">
                    {sortedMainRoster.map((card) => renderCardRow(card, false))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Subs section */}
          {sortedSubs.length > 0 && (
            <div className="space-y-3 mt-8">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                <UserPlus size={18} className="text-amber-600" aria-hidden />
                Subs
              </h3>
              <p className="text-xs text-gray-500">Fill-ins when roster players are out. Subs bat lower and get less playing time priority.</p>
              {viewMode === 'tiles' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {sortedSubs.map((card) => renderCardTile(card, true))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden border border-amber-100">
                  <div className="divide-y divide-gray-200">
                    {sortedSubs.map((card) => renderCardRow(card, true))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  function renderCardTile(card: PlayerCard, isSub?: boolean) {
    const avatarUrl = card.avatar_url || card.photo_url;
    const injuryStatus = (card.player?.injury_status as InjuryStatus) || 'H';
    return (
      <Link
        key={card.id}
        to={`/player-cards/${card.player_id}`}
        className="bg-white rounded-lg shadow-sm p-3 hover:shadow-md transition-shadow cursor-pointer border border-gray-100 relative"
      >
        {card.player?.number && (
          <div className="absolute top-2 left-2">
            <span className="text-lg font-bold text-primary">#{card.player.number}</span>
          </div>
        )}
        <div className="flex justify-center mb-2 mt-1">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={`${card.player?.first_name} ${card.player?.last_name}`}
              className="w-16 h-16 rounded-full object-cover border-2 border-primary"
            />
          ) : (
            <div className="bg-primary text-white rounded-full w-16 h-16 flex items-center justify-center font-bold text-lg">
              {card.player?.number || <User size={24} />}
            </div>
          )}
        </div>
        <div className="text-center mb-1.5">
          <div className="flex items-center justify-center gap-1.5">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {card.player?.first_name} {card.player?.last_name}
            </h3>
            {isSub && (
              <UserPlus size={14} className="text-amber-600 flex-shrink-0" title="Sub" aria-label="Sub" />
            )}
          </div>
          {card.nickname && (
            <div className="text-xs text-gray-500 mt-0.5">({card.nickname})</div>
          )}
        </div>
        <div className="mb-2 flex justify-center">
          <PositionDisplay
            positions={card.eligible_positions}
            sport={currentTeam?.sport || 'Football'}
            maxDisplay={3}
            className="justify-center"
          />
        </div>
        {card.ovr_rating !== undefined && (
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-1">
              <span className="font-bold text-primary text-base">{card.ovr_rating}</span>
              <span className="text-gray-400 text-xs">OVR</span>
            </div>
            {(card.offense_designation || card.defense_designation) && (
              <div className="flex items-center justify-center gap-1.5 flex-wrap">
                {card.offense_designation && (
                  <span className="text-green-600 font-semibold text-xs px-1.5 py-0.5 bg-green-50 rounded">{card.offense_designation}</span>
                )}
                {card.defense_designation && (
                  <span className="text-blue-600 font-semibold text-xs px-1.5 py-0.5 bg-blue-50 rounded">{card.defense_designation}</span>
                )}
              </div>
            )}
          </div>
        )}
        {injuryStatus !== 'H' && (
          <div className="absolute top-2 right-2">
            <InjuryBadge injuryStatus={injuryStatus} size="xs" />
          </div>
        )}
        {card.player?.playParticipation?.hasMPR && (
          <div className={`absolute ${injuryStatus !== 'H' ? 'top-8 right-2' : 'top-2 right-2'}`}>
            <Shield size={16} className="text-primary" title={`MPR: ${card.player.playParticipation.minimumRequiredPlays || 0} minimum plays`} />
          </div>
        )}
      </Link>
    );
  }

  function renderCardRow(card: PlayerCard, isSub: boolean) {
    const avatarUrl = card.avatar_url || card.photo_url;
    const injuryStatus = (card.player?.injury_status as InjuryStatus) || 'H';
    return (
      <Link
        key={card.id}
        to={`/player-cards/${card.player_id}`}
        className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0">
          {card.player?.number || <User size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {card.player?.first_name} {card.player?.last_name}
            </h3>
            {isSub && (
              <UserPlus size={16} className="text-amber-600 flex-shrink-0" title="Sub" aria-label="Sub" />
            )}
            {injuryStatus !== 'H' && <InjuryBadge injuryStatus={injuryStatus} size="xs" />}
            {card.player?.playParticipation?.hasMPR && (
              <Shield size={16} className="text-primary flex-shrink-0" title={`MPR: ${card.player.playParticipation.minimumRequiredPlays || 0} minimum plays`} />
            )}
          </div>
          <div>
            <PositionDisplay positions={card.eligible_positions} sport={currentTeam?.sport || 'Football'} maxDisplay={5} />
          </div>
        </div>
        {card.ovr_rating !== undefined && (
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                <span className="font-bold text-primary text-base">{card.ovr_rating}</span>
                <span className="text-gray-400 text-xs">OVR</span>
              </div>
              {(card.offense_designation || card.defense_designation) && (
                <div className="flex items-center gap-1 justify-end mt-0.5">
                  {card.offense_designation && <span className="text-green-600 font-semibold text-xs">{card.offense_designation}</span>}
                  {card.defense_designation && <span className="text-blue-600 font-semibold text-xs">{card.defense_designation}</span>}
                </div>
              )}
            </div>
          </div>
        )}
      </Link>
    );
  }
};

